import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  CURRENCIES,
  CURRENCY_CODES,
  convertAmount,
  formatMoney,
  getStoredCurrency,
  getUsdToUgxRate,
  storeCurrency,
  toStorageUgx,
  DEFAULT_USD_TO_UGX,
} from '../../lib/currency';

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => getStoredCurrency());
  const [usdToUgx, setUsdToUgx] = useState(() => getUsdToUgxRate());

  const setCurrency = useCallback((code) => {
    if (!CURRENCIES[code]) return;
    setCurrencyState(code);
    storeCurrency(code);
  }, []);

  const formatPrice = useCallback(
    (amount, sourceCurrency = 'UGX', options = {}) =>
      formatMoney(amount, {
        sourceCurrency,
        displayCurrency: currency,
        usdToUgx,
        ...options,
      }),
    [currency, usdToUgx]
  );

  const convert = useCallback(
    (amount, from, to = currency) => convertAmount(amount, from, to, usdToUgx),
    [currency, usdToUgx]
  );

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      currencies: CURRENCIES,
      currencyCodes: CURRENCY_CODES,
      usdToUgx,
      setUsdToUgx,
      defaultRate: DEFAULT_USD_TO_UGX,
      formatPrice,
      convert,
      toStorageUgx: (amount, entryCurrency) => toStorageUgx(amount, entryCurrency, usdToUgx),
      meta: CURRENCIES[currency],
    }),
    [currency, setCurrency, usdToUgx, formatPrice, convert]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback if provider missing
    return {
      currency: 'UGX',
      setCurrency: () => {},
      currencies: CURRENCIES,
      currencyCodes: CURRENCY_CODES,
      usdToUgx: DEFAULT_USD_TO_UGX,
      setUsdToUgx: () => {},
      defaultRate: DEFAULT_USD_TO_UGX,
      formatPrice: (amount, source = 'UGX') =>
        formatMoney(amount, { sourceCurrency: source, displayCurrency: 'UGX' }),
      convert: (amount, from, to) => convertAmount(amount, from, to),
      toStorageUgx: (amount, entry) => toStorageUgx(amount, entry),
      meta: CURRENCIES.UGX,
    };
  }
  return ctx;
}

export default CurrencyContext;
